import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";
import NotAuthorised from "../../NotAuthorised";
import {useAuth} from "../../../context/AuthContext";
import QuizTypeForm from "../Form";
import {QuizTypeData} from "../../../types/QuizType";

function MutateQuizType() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: quizType, isLoading} = useQuery(
    ['quiz-types', {id}],
    () => ngage.quizTypes.get(Number(id)),
    {enabled: !!id && auth.can('quiz-types:read')}
  )

  const store = useMutation((data: QuizTypeData) => ngage.quizTypes.store(data))
  const update = useMutation((data: QuizTypeData) => ngage.quizTypes.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: QuizTypeData, {setSubmitting}: FormikHelpers<QuizTypeData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('quiz-types.all')

    setSubmitting(false)
    navigate('/quiz-types')
  }

  // Handle store mutation
  async function handleStore(values: QuizTypeData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Quiz Type')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Quiz Type')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: QuizTypeData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Quiz Type')} Updated`, `${values.name} has been updated`)
      await invalidate(['quiz-types', {id}])
    } catch (error) {
      notify("error", `${termFor('Quiz Type')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('quiz-types:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Interactive': '/',
    [termFor('Quiz Types')]: '/quiz-types',
    [`New ${termFor('Quiz Type')}`]: '/quiz-types/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Interactive': '/',
    [termFor('Quiz Types')]: '/quiz-types',
    [`Edit ${termFor('Quiz Type')}`]: `/quiz-types/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Quiz Type')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <QuizTypeForm quizType={quizType} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateQuizType;